import { createContext, JSX, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { Outlet } from 'react-router-dom';

import useEnv from '@zenbusiness/application-commons-env';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import qhash from '@zenbusiness/application-commons-utils/qhash';

import PAGE_CONFIG from '#client/config/flowPageConfig';
import useFlowConfigMultipleExperiments from '#client/hooks/useFlowConfigMultipleExperiments';

/** @typedef {import('react')} React */
/** @typedef {import('../config/FlowConfigBase').FlowConfigOptions} FlowConfigOptions */

/**
 * @typedef {Omit<FlowConfigOptions, 'priceListUuids'> & {
 *   pages: Array<object>,
 *   hash: string,
 *   priceListUuid: string,
 *   getPageConfig: function(string): object
 * }} FlowConfigContextValue
 */

/** @type {React.Context<FlowConfigContextValue>} */
export const FlowConfigContext = createContext();

/**
 * FlowConfigProvider component to provide the configuration for a flow.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The children components.
 * @param {object} props.flowConfig The configuration for the flow
 * @returns {JSX.Element} The provider component.
 */
export function FlowConfigProvider({ children, flowConfig }) {
  const { DEPLOY_ENV } = useEnv();
  const config = useFlowConfigMultipleExperiments(flowConfig);

  const { pageKeys, priceListUuids } = config;
  const priceListUuid = priceListUuids[DEPLOY_ENV === 'production' ? 'production' : 'development'];

  // if config is altered via experiments, hash will change
  const hash = qhash(
    JSON.stringify(config, (_key, value) => (value instanceof Set ? [...value] : value))
  );

  const pages = useMemo(() => {
    return pageKeys.map((key) => {
      const pageConfig = PAGE_CONFIG[key];
      if (!pageConfig) {
        throw new Error(`Page config for '${key}' not found`);
      }
      return pageConfig;
    });
  }, [pageKeys]);

  const getPageConfig = useCallback(
    (flowPage) => pages.find((page) => page.path === flowPage) ?? null,
    [pages]
  );

  const configContext = useMemoObject({
    ...config,
    hash,
    priceListUuid,
    pages,
    getPageConfig
  });

  return (
    <FlowConfigContext.Provider value={configContext}>
      {children || <Outlet />}
    </FlowConfigContext.Provider>
  );
}

FlowConfigProvider.propTypes = {
  flowConfig: PropTypes.object.isRequired
};
